<template>
  <div class="request-quotes-modal">
    <div
      v-if="!subhire"
      class="p-4 text-center text-surface-500"
    >
      <p>No subhire data available</p>
    </div>
    <template v-else>
      <div class="mb-4">
        <h3 class="text-lg font-semibold mb-1">
          Request availability &amp; quotes
        </h3>
        <p class="text-sm text-surface-600 dark:text-surface-400">
          Subhire: <strong>{{ subhire.name || subhire.subhire_number || (subhire.id ? `SH-${subhire.id}` : 'N/A') }}</strong><span
            v-if="subhire.name && (subhire.subhire_number || subhire.id)"
            class="text-surface-500"
          > ({{ subhire.subhire_number || `SH-${subhire.id}` }})</span>
        </p>
      </div>

      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium mb-2">Suppliers</label>
          <p class="text-sm text-surface-700 dark:text-surface-300">
            {{ supplierNamesDisplay }}
          </p>
        </div>

        <div>
          <label class="block text-sm font-medium mb-2">Message (optional)</label>
          <TextArea
            v-model="message"
            rows="3"
            placeholder="Optional message to include in the request email..."
            class="w-full"
          />
        </div>

        <div class="rounded-lg border border-surface-200 dark:border-surface-600 p-4 space-y-4 bg-surface-50 dark:bg-surface-800/50">
          <label class="block text-sm font-medium">Delivery &amp; collection</label>
          <p class="text-xs text-surface-500 dark:text-surface-400">
            Stored on the subhire and included in the request email.
          </p>
          <div>
            <label class="block text-xs font-medium text-surface-500 mb-1">Who delivers and collects?</label>
            <Select
              v-model="deliveryPreference"
              :options="deliveryPreferenceOptions"
              option-label="label"
              option-value="value"
              placeholder="Select"
              class="w-full"
            />
          </div>
          <template v-if="deliveryPreference === 'advanced'">
            <div class="border-t border-surface-200 dark:border-surface-600 pt-4 mt-2">
              <label class="block text-xs font-medium text-surface-500 mb-1">Inbound &amp; outbound methods</label>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span class="text-xs text-surface-500 dark:text-surface-400 block mb-1">Inbound (how equipment comes to you)</span>
                  <Select
                    v-model="inboundMethod"
                    :options="inboundMethodOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="Optional"
                    class="w-full"
                    show-clear
                  />
                </div>
                <div>
                  <span class="text-xs text-surface-500 dark:text-surface-400 block mb-1">Outbound (how equipment goes back)</span>
                  <Select
                    v-model="outboundMethod"
                    :options="outboundMethodOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="Optional"
                    class="w-full"
                    show-clear
                  />
                </div>
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-surface-200 dark:border-surface-600 pt-4">
              <div>
                <label class="block text-xs font-medium text-surface-500 mb-1">Delivery date (when you receive equipment)</label>
                <Calendar
                  v-model="deliveryDate"
                  date-format="dd/mm/yy"
                  placeholder="Select delivery date"
                  class="w-full"
                  show-icon
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-surface-500 mb-1">Return date (when you return equipment)</label>
                <Calendar
                  v-model="returnDate"
                  date-format="dd/mm/yy"
                  placeholder="Select return date"
                  class="w-full"
                  show-icon
                />
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="flex justify-end gap-2 mt-6 pt-4 border-t border-surface-200">
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          @click="handleCancel"
        />
        <Button
          label="Send requests"
          icon="pi pi-send"
          :loading="sending"
          :disabled="!selectedSupplierIds?.length"
          @click="handleSendRequests"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useToastService } from '@/composables/useToastService'
import { projectStore } from '@/components/project/officeProject/projectContents/projectStore'
import { sendSubhireRequest } from '@/services/subhireRequestsService'
import { getEquipmentListContent } from '@/services/projectService'
import { LocalAssetService } from '@/services/localAssetService'

const props = defineProps({
  subhire: {
    type: Object,
    default: () => null
  },
  projectId: {
    type: [String, Number],
    default: null
  },
  equipmentListId: {
    type: [String, Number],
    default: null
  },
  /** When opening from "Search for supplier", pre-select these supplier ids. */
  initialSupplierIds: {
    type: Array,
    default: () => []
  },
  /** Optional supplier objects { id, company_name } so the MultiSelect can show names. */
  initialSuppliers: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'generated'])

const toast = useToastService()

const supplierOptions = ref([])
const suppliersLoading = ref(false)
const selectedSupplierIds = ref([])

const supplierNamesDisplay = computed(() => {
  const ids = selectedSupplierIds.value
  if (!ids?.length) return 'No suppliers selected.'
  if (props.initialSuppliers?.length) {
    const byId = new Map(props.initialSuppliers.map((s) => [Number(s.id ?? s.value), s.company_name || s.name || `Supplier ${s.id}`]))
    const names = ids.map((id) => byId.get(Number(id))).filter(Boolean)
    if (names.length) return `Request will be sent to: ${names.join(', ')}`
  }
  const opts = supplierOptions.value
  if (opts.length) {
    const byValue = new Map(opts.map((o) => [o.value, o.label]))
    const names = ids.map((id) => byValue.get(Number(id))).filter(Boolean)
    if (names.length) return `Request will be sent to: ${names.join(', ')}`
  }
  return `Request will be sent to ${ids.length} supplier(s).`
})
const message = ref('')
const deliveryPreference = ref('we_deliver_collect')
const inboundMethod = ref(null)
const outboundMethod = ref(null)
const deliveryDate = ref(null)
const returnDate = ref(null)
const sending = ref(false)

const deliveryPreferenceOptions = [
  { label: 'We deliver and collect', value: 'we_deliver_collect' },
  { label: 'You deliver and collect', value: 'you_deliver_collect' },
  { label: 'Advanced', value: 'advanced' }
]

const inboundMethodOptions = [
  { label: 'Supplier delivers', value: 'supplier_delivers' },
  { label: 'We collect', value: 'we_collect' },
  { label: 'Courier', value: 'courier' }
]

const outboundMethodOptions = [
  { label: 'Supplier collects', value: 'supplier_collects' },
  { label: 'We return', value: 'we_return' },
  { label: 'Courier', value: 'courier' }
]

function toISODateString(val) {
  if (!val) return undefined
  if (val instanceof Date) return val.toISOString().split('T')[0]
  const s = String(val)
  return s.split('T')[0] || undefined
}

function collectAssetsWithLocalId(content) {
  const out = []
  if (!Array.isArray(content)) return out
  const visit = (node) => {
    const id = node.local_asset_id ?? node.localAssetId
    if (id) {
      out.push({
        id: Number(id),
        name: node.display_name || node.name || `Asset ${id}`
      })
    }
    const children = node.children || node.tasks || node.items || node.content || []
    for (const child of children) {
      visit(child)
    }
  }
  for (const node of content) {
    visit(node)
  }
  return [...new Map(out.map((a) => [a.id, a])).values()]
}

async function loadSuppliers() {
  const pid = props.projectId ?? projectStore.currentProjectId
  const listId = props.equipmentListId ?? projectStore.currentEquipmentListId
  if (!pid || !listId) {
    supplierOptions.value = []
    return
  }
  suppliersLoading.value = true
  supplierOptions.value = []
  try {
    const listRes = await getEquipmentListContent(pid, listId)
    const raw = listRes.data
    const content = raw?.content ?? raw?.data?.content ?? raw?.groups ?? []
    const assets = collectAssetsWithLocalId(content)
    const byId = new Map()
    for (const asset of assets) {
      try {
        const costRes = await LocalAssetService.getSupplierCosts(asset.id)
        const payload = costRes.data
        const costs = Array.isArray(payload) ? payload : (payload?.supplier_costs ?? [])
        if (!Array.isArray(costs) || costs.length === 0) continue
        for (const sc of costs) {
          const supplierId = sc.supplier_id ?? sc.supplier?.id
          if (!supplierId) continue
          const name = sc.supplier?.company_name || sc.supplier?.name || '—'
          if (!byId.has(supplierId)) {
            byId.set(supplierId, { value: Number(supplierId), label: name })
          }
        }
      } catch {
        // skip
      }
    }
    let options = [...byId.values()]
    if (props.initialSuppliers?.length) {
      const existing = new Set(options.map((o) => o.value))
      for (const s of props.initialSuppliers) {
        const id = s.id ?? s.value
        if (id != null && !existing.has(id)) {
          existing.add(id)
          options.push({ value: Number(id), label: s.company_name || s.name || `Supplier ${id}` })
        }
      }
    }
    supplierOptions.value = options.sort((a, b) => (a.label || '').localeCompare(b.label || ''))
  } catch (err) {
    console.error('Error loading suppliers for request quotes:', err)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err.response?.data?.message || 'Failed to load suppliers',
      life: 4000
    })
  } finally {
    suppliersLoading.value = false
  }
}

async function handleSendRequests() {
  const ids = selectedSupplierIds.value
  if (!ids?.length || !props.subhire?.id) return

  const projectId = props.projectId ?? projectStore.currentProjectId
  const equipmentListId = props.equipmentListId ?? projectStore.currentEquipmentListId
  if (!projectId || !equipmentListId) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Project and equipment list are required.',
      life: 3000
    })
    return
  }

  const lineIds = (props.subhire.lines || []).map((l) => l.id).filter(Boolean)
  const payload = {
    message: message.value?.trim() || undefined,
    line_ids: lineIds.length ? lineIds : undefined
  }
  if (deliveryPreference.value === 'advanced') {
    payload.inbound_method = inboundMethod.value || undefined
    payload.outbound_method = outboundMethod.value || undefined
    payload.delivery_date = toISODateString(deliveryDate.value)
    payload.return_date = toISODateString(returnDate.value)
  } else {
    payload.delivery_preference = deliveryPreference.value || undefined
  }

  sending.value = true
  let sent = 0
  let failed = 0
  try {
    for (const supplierId of ids) {
      try {
        await sendSubhireRequest(projectId, equipmentListId, props.subhire.id, {
          ...payload,
          supplier_id: supplierId
        })
        sent += 1
      } catch (err) {
        failed += 1
        const msg = err?.response?.data?.message ?? err?.message ?? 'Request failed'
        toast.add({
          severity: 'error',
          summary: `Supplier request failed`,
          detail: msg,
          life: 4000
        })
      }
    }
    if (sent > 0) {
      toast.add({
        severity: 'success',
        summary: 'Requests sent',
        detail: failed > 0
          ? `${sent} request(s) sent. ${failed} failed.`
          : `${sent} request(s) sent. Suppliers will receive an email with a link to respond.`,
        life: 4000
      })
      emit('generated', { type: 'quote_requests', sent, failed })
      handleCancel()
    }
  } finally {
    sending.value = false
  }
}

function handleCancel() {
  emit('close')
}

onMounted(() => {
  loadSuppliers()
})

watch(
  () => [props.projectId, props.equipmentListId],
  () => {
    loadSuppliers()
  }
)

watch(
  () => props.initialSupplierIds,
  (ids) => {
    if (ids?.length) selectedSupplierIds.value = ids.map((id) => Number(id))
  },
  { immediate: true }
)
</script>

<style scoped>
.request-quotes-modal {
  padding: 0.25rem;
}
</style>
